/* eslint-disable react/prop-types -- Props are typed with TypeScript interfaces */
import clsx from 'clsx';
import { addMonths, addWeeks, format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';
import {
  Button,
  CircularProgress,
  Icon,
  IconButton,
  MenuItem,
  PageHeaderLayout,
  Select,
  Typography,
} from '@/ui';

import HideCancelledToggle from './HideCancelledToggle';
import MembersSidebar from './MembersSidebar';
import { dayKeyFromIso, formatDateKey } from './calendarGrid';
import { useScheduleState } from './useScheduleState';
import ListView from './views/ListView';
import MonthView from './views/MonthView';
import WeekView from './views/WeekView';

import type { ScheduleViewMode } from './types';
import type { User } from '@/types';

import { usesMomentsScheduling } from '@/features/appointments/schedulingProvider';
import { resolveMomentsBaseUrl } from '@/store/api/momentsApi';
import { useGetUserQuery } from '@/store/api/userApi';

const VIEW_OPTIONS: { label: string; value: ScheduleViewMode }[] = [
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
  { label: 'List', value: 'list' },
];

const getViewerTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

// Coach Schedule page (MEN-4160). Shown for coaches whose scheduling provider is
// Moments; other coaches fall back to the legacy sessions list. Default view is
// the calendar Week view.
interface CoachSchedulePageProps {
  embeddedCoach?: User;
}

export interface CoachScheduleLayoutProps {
  adminCoachId?: string;
  adminMode?: boolean;
  /** MEN-5473/MEN-5635: the signed-in coach's own id, for the member booking menu. */
  bookingLinkCoachId?: string;
  calendarSettingsUrl?: string;
  children: React.ReactNode;
  displayedTimezone: string;
  embedded?: boolean;
  hideCancelled?: boolean;
  isListView?: boolean;
  members: { fullName: string; id: string }[];
  onHideCancelledChange?: (hideCancelled: boolean) => void;
  onMovePeriod?: (direction: -1 | 1) => void;
  onSeeAll: () => void;
  onSelectMember: (memberId: string) => void;
  onSetViewMode: (mode: ScheduleViewMode) => void;
  onToday?: () => void;
  periodLabel: string;
  selectedMemberId: string | null;
  timezoneLabel: string;
  viewLocked?: boolean;
  viewMode: ScheduleViewMode;
}

export const CoachScheduleLayout: React.FC<CoachScheduleLayoutProps> = ({
  adminCoachId,
  adminMode = false,
  bookingLinkCoachId,
  calendarSettingsUrl,
  children,
  displayedTimezone,
  embedded = false,
  hideCancelled = false,
  isListView = false,
  members,
  onHideCancelledChange,
  onMovePeriod,
  onSeeAll,
  onSelectMember,
  onSetViewMode,
  onToday,
  periodLabel,
  selectedMemberId,
  timezoneLabel,
  viewLocked = false,
  viewMode,
}) => {
  // MEN-4239: Figma keeps the Members rail flush with the top of the card and
  // starts the toolbar beside it, above the grid only — not as a full-width
  // band across both columns.
  const toolbar = (
    <div
      className="flex items-center justify-between gap-4 bg-white px-4 py-3 max-md:flex-col max-md:items-stretch"
      data-testid="schedule-toolbar"
    >
      <div className="flex items-center gap-3">
        <Button variant="outlined" onClick={onToday}>
          Today
        </Button>
        <IconButton
          aria-label="Previous month"
          disabled={isListView}
          size="small"
          onClick={() => onMovePeriod?.(-1)}
        >
          <Icon>chevron_left</Icon>
        </IconButton>
        <Typography data-testid="schedule-period-label" variant="subtitle2">
          {periodLabel}
        </Typography>
        <IconButton
          aria-label="Next month"
          disabled={isListView}
          size="small"
          onClick={() => onMovePeriod?.(1)}
        >
          <Icon>chevron_right</Icon>
        </IconButton>
      </div>

      {/* MEN-5349: Figma centers the toggle between the period controls and
          the time zone; an empty slot keeps the right group pinned in Week and
          Month views. */}
      <div className="flex items-center">
        {isListView && onHideCancelledChange && (
          <HideCancelledToggle
            checked={hideCancelled}
            onChange={onHideCancelledChange}
          />
        )}
      </div>

      <div className="flex items-center gap-3 max-md:flex-wrap">
        {/* The narrower coach-profile toolbar stacks the zone under the label. */}
        <Typography
          className={clsx(
            'text-cement-300',
            embedded && 'flex flex-col text-right',
          )}
          data-testid="schedule-timezone"
          variant="caption"
        >
          {embedded ? (
            <>
              <span>{`Times shown in ${timezoneLabel}`}</span>
              <span>{displayedTimezone}</span>
            </>
          ) : (
            `Times shown in ${timezoneLabel} - ${displayedTimezone}`
          )}
        </Typography>
        <Select
          disabled={viewLocked}
          inputProps={{ 'data-testid': 'schedule-view-select' }}
          size="small"
          value={viewMode}
          onChange={(event) =>
            onSetViewMode(event.target.value as ScheduleViewMode)
          }
        >
          {VIEW_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </div>
    </div>
  );

  const scheduleWorkspace = (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-solid border-cement-200 bg-white"
      data-testid="schedule-workspace"
      data-variant={embedded ? 'embedded' : 'standalone'}
    >
      <div
        className="flex min-h-0 flex-1 overflow-y-auto overflow-x-clip bg-white max-md:flex-col"
        data-testid="schedule-calendar-surface"
      >
        <MembersSidebar
          adminCoachId={adminCoachId}
          bookingLinkCoachId={bookingLinkCoachId}
          members={members}
          selectedMemberId={selectedMemberId}
          onSeeAll={onSeeAll}
          onSelectMember={onSelectMember}
        />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {toolbar}
          {/* MEN-4239: the week and month grids are edge-to-edge by design —
              their own strokes are the gutter. The List stream is prose, so it
              needs the toolbar's own inset, otherwise the date column jams
              against the Members rail divider. */}
          <div
            className={clsx(
              'min-h-0 min-w-0 flex-1 max-md:min-h-80',
              isListView && 'px-4 pb-4',
            )}
            data-testid="schedule-calendar-view"
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  if (embedded) return scheduleWorkspace;

  return (
    <div
      className="flex-1 overflow-x-hidden bg-sunny-100"
      data-testid="schedule-page"
    >
      <div className="container mx-auto flex h-full max-w-screen-xl flex-1 flex-col px-2 py-8">
        <Typography
          className="font-bold"
          data-testid="schedule-context-label"
          variant="caption"
        >
          Schedule
        </Typography>
        <PageHeaderLayout
          subtitle="See booked sessions and reservations."
          title="Schedule"
        >
          {!adminMode && (
            <Button
              disabled={!calendarSettingsUrl}
              href={calendarSettingsUrl}
              rel="noopener noreferrer"
              target="_blank"
              variant="outlined"
            >
              Calendar Settings
            </Button>
          )}
        </PageHeaderLayout>
        {scheduleWorkspace}
      </div>
    </div>
  );
};

const CoachScheduleWorkspace: React.FC<CoachSchedulePageProps> = ({
  embeddedCoach,
}) => {
  const { coachId: routeCoachId } = useParams();
  const selectedCoachId = embeddedCoach?.id ?? routeCoachId;
  const isEmbedded = Boolean(embeddedCoach);
  const isAdminMode = Boolean(selectedCoachId);
  const { user: currentUser, isUserLoading } = useCurrentUser();
  const {
    currentData: selectedCoach,
    isError: isSelectedCoachError,
    isFetching: isSelectedCoachFetching,
    isLoading: isSelectedCoachLoading,
  } = useGetUserQuery(selectedCoachId ?? '', {
    skip: !isAdminMode || isEmbedded,
  });
  const isSelectedCoachPending =
    isAdminMode &&
    !isEmbedded &&
    !isSelectedCoachError &&
    (isSelectedCoachLoading || isSelectedCoachFetching || !selectedCoach);
  const user = embeddedCoach ?? selectedCoach ?? currentUser;
  const coachId = selectedCoachId ?? user.id;
  const momentsBaseUrl = resolveMomentsBaseUrl({
    envUrl: import.meta.env.VITE_MOMENTS_BASE_URL,
    fallbackUrl: user.momentsBaseUrl,
  });
  const calendarSettingsUrl = momentsBaseUrl
    ? `${momentsBaseUrl}/settings/calendars`
    : undefined;

  const isCalendarEnabled = isEmbedded || usesMomentsScheduling(user);

  const schedule = useScheduleState();
  const [hideCancelled, setHideCancelled] = useState(false);
  const [viewDate, setViewDate] = useState<Date>(() => new Date());
  const [calendarTimeZone, setCalendarTimeZone] = useState<string | null>(null);
  const [shouldScrollToday, setShouldScrollToday] = useState(false);
  // Calendar layout uses the browser's local Date APIs; keep the toolbar honest.
  const viewerTimezone = useMemo(getViewerTimezone, []);
  const profileTimezone = isAdminMode
    ? currentUser.profile?.timezone
    : user.profile?.timezone;
  const defaultTimezone = profileTimezone ?? viewerTimezone;
  const timezone = isAdminMode
    ? defaultTimezone
    : (calendarTimeZone ?? defaultTimezone);
  const timezoneLabel = isAdminMode ? 'your timezone' : 'Coach’s time zone';
  const displayedTimezone = isAdminMode
    ? timezone
    : timezone.replaceAll('_', ' ').replaceAll('/', ' / ');
  const isListView = schedule.viewMode === 'list';
  const listTodayKey = dayKeyFromIso(new Date().toISOString(), timezone);
  const periodLabel = useMemo(
    () =>
      isListView
        ? `Today · ${formatDateKey(listTodayKey)}`
        : format(viewDate, 'MMMM yyyy'),
    [isListView, listTodayKey, viewDate],
  );

  const handleTimeZoneChange = useCallback((next?: string | null) => {
    if (next) setCalendarTimeZone(next);
  }, []);

  const members = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- useCurrentUser defaults to `{} as User` while loading, so coachees can be undefined at runtime despite the type
      (user.coachees ?? []).map((coachee) => ({
        fullName: coachee.fullName,
        id: coachee.id,
      })),
    [user.coachees],
  );

  useEffect(() => {
    if (!shouldScrollToday) return;

    document
      .getElementById('schedule-today-anchor')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setShouldScrollToday(false);
  }, [shouldScrollToday, viewDate]);

  if (!isEmbedded && isAdminMode && isSelectedCoachError) {
    return <Navigate to={`/coach/manage/coaches/${coachId}`} replace />;
  }

  if (isUserLoading || isSelectedCoachPending) {
    return <CircularProgress data-testid="schedule-loading" />;
  }

  if (!isCalendarEnabled) {
    return (
      <Navigate
        to={
          isAdminMode
            ? `/coach/manage/coaches/${coachId}`
            : '/coach/sessions/upcoming'
        }
        replace
      />
    );
  }

  const handleSelectMember = schedule.selectMember;
  const handleSeeAll = schedule.clearMember;
  const handleSetViewMode = schedule.setViewMode;
  const handleToday = () => {
    setViewDate(new Date());
    setShouldScrollToday(true);
  };
  // MEN-4674: the toolbar arrows sit next to the month-name label
  // (`format(viewDate, 'MMMM yyyy')`), so they must always move by MONTH,
  // even in Week view. Week-to-week navigation lives on the WeekView day
  // header (see `onNavigateWeek` below), matching the Figma design.
  const movePeriod = (direction: -1 | 1) => {
    if (isListView) return;
    setViewDate((current) => addMonths(current, direction));
  };

  const navigateWeek = (direction: -1 | 1) => {
    setViewDate((current) => addWeeks(current, direction));
  };

  const viewProps = {
    adminCoachId: isAdminMode ? coachId : undefined,
    coachId,
    hideCancelled,
    onNavigateWeek: navigateWeek,
    onTimeZoneChange: handleTimeZoneChange,
    selectedMemberId: schedule.selectedMemberId,
    timeZone: timezone,
    viewDate,
  };

  return (
    <CoachScheduleLayout
      adminCoachId={isAdminMode ? coachId : undefined}
      adminMode={isAdminMode}
      // MEN-5473: members open these links under the coach id their own
      // coach navigation uses, which is the signed-in coach's user id.
      bookingLinkCoachId={isAdminMode ? undefined : currentUser.id}
      calendarSettingsUrl={calendarSettingsUrl}
      displayedTimezone={displayedTimezone}
      embedded={isEmbedded}
      hideCancelled={hideCancelled}
      isListView={isListView}
      members={members}
      periodLabel={periodLabel}
      selectedMemberId={schedule.selectedMemberId}
      timezoneLabel={timezoneLabel}
      viewLocked={schedule.isViewLocked}
      viewMode={schedule.viewMode}
      onHideCancelledChange={setHideCancelled}
      onMovePeriod={movePeriod}
      onSeeAll={handleSeeAll}
      onSelectMember={handleSelectMember}
      onSetViewMode={handleSetViewMode}
      onToday={handleToday}
    >
      {schedule.viewMode === 'week' && <WeekView {...viewProps} />}
      {schedule.viewMode === 'month' && <MonthView {...viewProps} />}
      {schedule.viewMode === 'list' && <ListView {...viewProps} />}
    </CoachScheduleLayout>
  );
};

const CoachSchedulePage: React.FC<CoachSchedulePageProps> = ({
  embeddedCoach,
}) => {
  const { coachId } = useParams();
  return (
    <CoachScheduleWorkspace
      key={embeddedCoach?.id ?? coachId ?? 'current-coach'}
      embeddedCoach={embeddedCoach}
    />
  );
};

export default CoachSchedulePage;
/* eslint-enable react/prop-types */
